import React, { useEffect, useMemo, useRef, useState } from 'react';
import AppStoreLink from '../../../components/common/AppStoreLink';
import WhatsAppBookingLink from '../../../components/common/WhatsAppBookingLink';
import BookingCta from '../../booking/BookingCta';
import { WEB_BOOKING_ENABLED } from '../../../config/env';
import { INSTANT_MAX_DISTANCE_KM, requiresScheduledBooking } from '../../../config/bookingPolicy';
import {
  RATE_CARD,
  SAMPLE_ROUTE,
  formatCapacity,
  formatRand,
  isBookableVehicle,
  normalizeVehicleType,
} from '../../../config/rateCard';
import { trackEvent } from '../../../services/analytics';
import { ensureGooglePlacesLoaded } from '../../../services/googlePlaces';
import {
  EstimatePoint,
  PublicFareEstimateResponse,
  fetchPublicFareEstimate,
} from '../../../services/api/publicFareEstimates';

type PlacePrediction = {
  description: string;
  place_id: string;
};

type GoogleAutocompleteService = {
  getPlacePredictions: (
    request: Record<string, unknown>,
    callback: (predictions: PlacePrediction[] | null) => void,
  ) => void;
};

type GooglePlacesService = {
  getDetails: (
    request: Record<string, unknown>,
    callback: (place: any, status: string) => void,
  ) => void;
};

declare global {
  interface Window {
    google?: any;
  }
}

const SAMPLE_PICKUP: EstimatePoint = {
  lat: -32.985,
  lng: 27.871,
  address: SAMPLE_ROUTE.pickup,
};

const SAMPLE_DROPOFF: EstimatePoint = {
  lat: -32.912,
  lng: 27.742,
  address: SAMPLE_ROUTE.dropoff,
};

/**
 * The offline stand-in for one API row. Every field is the API's own value for
 * SAMPLE_ROUTE, including `vehicleType` — so a fallback row and a live row are
 * the same vehicle and a selection survives the switch between them. The fare
 * is measured rather than modelled: this widget must never quote a number the
 * booking engine would not.
 */
function estimateOptionFromRateCard(vehicle: (typeof RATE_CARD)[number]) {
  return {
    vehicleType: vehicle.vehicleType,
    displayName: vehicle.label,
    capacityKg: vehicle.capacityKg,
    estimated_fare: vehicle.sampleFare,
  };
}

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return 'Trip time pending';
  const totalMinutes = Math.max(1, Math.round(seconds / 60));
  if (totalMinutes < 60) return `Trip ${totalMinutes} min`;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes ? `Trip ${hours} hr ${minutes} min` : `Trip ${hours} hr`;
}

function formatApiMoney(amount: number | null | undefined): string {
  return Number.isFinite(Number(amount)) ? formatRand(Math.round(Number(amount))) : 'Estimate pending';
}

function shortAddress(address: string): string {
  return address.split(',').map((part) => part.trim()).filter(Boolean).slice(0, 2).join(', ') || address;
}

/** Identifies the exact route a set of figures was computed for. */
function routeKey(pickup: EstimatePoint, dropoff: EstimatePoint): string {
  return `${pickup.lat},${pickup.lng}|${dropoff.lat},${dropoff.lng}`;
}

const SAMPLE_ROUTE_KEY = routeKey(SAMPLE_PICKUP, SAMPLE_DROPOFF);

const EstimateWidget: React.FC = () => {
  const [pickup, setPickup] = useState<EstimatePoint>(SAMPLE_PICKUP);
  const [dropoff, setDropoff] = useState<EstimatePoint>(SAMPLE_DROPOFF);
  const [pickupText, setPickupText] = useState(SAMPLE_PICKUP.address);
  const [dropoffText, setDropoffText] = useState(SAMPLE_DROPOFF.address);
  const [selectedVehicleType, setSelectedVehicleType] = useState('BAKKIE');
  /* The answer is stored with the route it answers for. Without that pairing a
     failed lookup leaves the previous route's figures on screen beside the new
     addresses — with the API down, a Cape Town route renders the East London
     sample fare. The app never does this: on failure `your-route` clears its
     estimates outright rather than let a stale price stand for a new route. */
  const [estimate, setEstimate] = useState<{
    route: string;
    data: PublicFareEstimateResponse;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [estimateError, setEstimateError] = useState('');
  const [placesReady, setPlacesReady] = useState(false);

  const fallbackEstimate = useMemo<PublicFareEstimateResponse>(
    () => ({
      currency: 'ZAR',
      distanceKm: SAMPLE_ROUTE.distanceKm,
      durationSec: SAMPLE_ROUTE.durationSec,
      routePolyline: null,
      estimates: RATE_CARD.map(estimateOptionFromRateCard),
      bookingAvailableInApp: true,
      estimate_only: true,
    }),
    [],
  );

  const currentRoute = routeKey(pickup, dropoff);
  const isCurrent = estimate?.route === currentRoute;

  /* The rate-card fallback only speaks for the sample route it was built from.
     Once the visitor has chosen their own addresses it is no longer an answer
     to their question, so its figures must not be shown as one. */
  const priced = isCurrent || currentRoute === SAMPLE_ROUTE_KEY;

  const activeEstimate = isCurrent ? estimate.data : fallbackEstimate;
  const selected =
    activeEstimate.estimates.find((vehicle) => vehicle.vehicleType === selectedVehicleType) ??
    activeEstimate.estimates[0];
  const whatsappPickupPoint = pickupText.trim() === pickup.address.trim() ? pickup : null;
  const whatsappDropoffPoint = dropoffText.trim() === dropoff.address.trim() ? dropoff : null;

  const statusLabel = loading
    ? 'Calculating'
    : !priced
      ? 'Unavailable'
      : activeEstimate.estimate_only
        ? 'Estimate only'
        : 'Live route';

  useEffect(() => {
    ensureGooglePlacesLoaded()
      .then(() => setPlacesReady(true))
      .catch(() => setPlacesReady(false));
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadEstimate() {
      try {
        setLoading(true);
        setEstimateError('');
        const raw = await fetchPublicFareEstimate({ pickup, dropoff });
        if (cancelled) return;
        /* The engine prices every class it knows; the site may only show the
           ones the app can actually book. Filtering here rather than at render
           keeps the selected vehicle, the headline fare and the tracked events
           all working from the same bookable set.
           Deduplicated by normalised type because the engine returns SEMI_TRUCK
           and HORSE_AND_TRAILER separately while the app treats them as one
           vehicle — without this, enabling Horse & Trailer would list it twice. */
        const seen = new Set<string>();
        const estimates = raw.estimates.filter((item) => {
          if (!isBookableVehicle(item.vehicleType)) return false;
          const key = normalizeVehicleType(item.vehicleType);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        const response = { ...raw, estimates };
        setEstimate({ route: routeKey(pickup, dropoff), data: response });
        /* Shape of the trip only — never the addresses that produced it. */
        trackEvent('estimate_priced', {
          distance_km: Math.round(response.distanceKm),
          vehicle_count: response.estimates.length,
          requires_scheduled: requiresScheduledBooking(response.distanceKm),
          is_sample_route: routeKey(pickup, dropoff) === SAMPLE_ROUTE_KEY,
        });
        if (response.estimates.length) {
          setSelectedVehicleType((current) =>
            response.estimates.some((item) => item.vehicleType === current)
              ? current
              : response.estimates[0].vehicleType,
          );
        }
      } catch (error: any) {
        if (!cancelled) {
          /* Never surface the raw failure. A network error's message is
             "Failed to fetch", which tells a customer nothing and reads as a
             broken page; the service's own message is the only one written for
             a reader. */
          const friendly =
            typeof error?.message === 'string' && error.message.includes('route estimate')
              ? error.message
              : 'Could not calculate this route estimate right now.';
          setEstimateError(friendly);
          /* Failure rate is the point: the fallback hides an outage from the
             visitor, so without this the funnel looks healthy while nobody is
             getting a real price. */
          trackEvent('estimate_failed');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadEstimate();

    return () => {
      cancelled = true;
    };
  }, [pickup, dropoff]);

  return (
    <div className="sw-estimate">
      <div className="sw-estimate-head">
        <span className="sw-eyebrow">Route estimate</span>
        {/* Quiet variant when there is no figure to qualify — the Route colour
            reads as "this is priced", which is the opposite of the truth here. */}
        <span className={`sw-pill ${priced ? 'sw-pill-live' : 'sw-pill-quiet'}`}>
          {statusLabel}
        </span>
      </div>

      <PlaceEstimateField
        dotClassName="sw-route-dot-pickup"
        label="Pickup"
        placesReady={placesReady}
        text={pickupText}
        onTextChange={setPickupText}
        onPlaceSelected={(point) => {
          setPickup(point);
          setPickupText(point.address);
        }}
      />
      <PlaceEstimateField
        dotClassName="sw-route-dot-drop"
        label="Drop-off"
        placesReady={placesReady}
        text={dropoffText}
        onTextChange={setDropoffText}
        onPlaceSelected={(point) => {
          setDropoff(point);
          setDropoffText(point.address);
        }}
      />

      <fieldset className="sw-vehicle-set">
        <legend className="sw-vehicle-legend">Choose your truck</legend>
        {activeEstimate.estimates.map((vehicle) => (
          <label
            className={`sw-vehicle-row${
              vehicle.vehicleType === selectedVehicleType ? ' sw-vehicle-row-on' : ''
            }`}
            key={vehicle.vehicleType}
          >
            <input
              type="radio"
              name="sw-vehicle"
              value={vehicle.vehicleType}
              checked={vehicle.vehicleType === selectedVehicleType}
              onChange={() => {
                setSelectedVehicleType(vehicle.vehicleType);
                /* Which classes visitors actually price — the demand signal the
                   fleet table could not give while it listed three of seven. */
                trackEvent('estimate_vehicle_selected', { vehicle_type: vehicle.vehicleType });
              }}
            />
            <span className="sw-vehicle-name">
              <strong>{vehicle.displayName}</strong> - {formatCapacity(vehicle.capacityKg)}
            </span>
            <span className="sw-vehicle-price">
              {priced ? formatApiMoney(vehicle.estimated_fare) : '—'}
            </span>
          </label>
        ))}
      </fieldset>

      <div className="sw-estimate-total">
        <div>
          <span className="sw-estimate-total-label">Estimated fare</span>
          <span className="sw-estimate-total-meta">
            {priced
              ? `${activeEstimate.distanceKm} km - ${formatDuration(activeEstimate.durationSec)}`
              : 'No estimate for this route yet'}
          </span>
        </div>
        <span className="sw-estimate-figure" aria-live="polite">
          {loading ? '...' : priced ? formatApiMoney(selected?.estimated_fare) : '—'}
        </span>
      </div>

      {estimateError ? <p className="sw-estimate-error">{estimateError}</p> : null}

      {/* The fare is right; the dispatch mode is not what the page implies.
          Past the instant limit the app plans the trip as a scheduled booking
          instead, and says so on its own `schedule-required` screen. Saying it
          here keeps the estimate from reading as "a driver is minutes away". */}
      {priced && requiresScheduledBooking(activeEstimate.distanceKm) ? (
        <p className="sw-estimate-note">
          Over {INSTANT_MAX_DISTANCE_KM} km this is a <strong>scheduled booking</strong>, not an
          instant trip — you pick the date and time in the app.
        </p>
      ) : null}

      {/* Coverage is stated where the number is read, not six sections later.
          Deliberately NOT a computed in/out-of-area check: the product has no
          service-area concept — the app decides availability on driver supply
          alone (`getCarTypeEstimates` returning nothing) and has no coverage
          logic anywhere. Inventing a radius here would enforce a boundary the
          product does not actually have. This states the fact and gives the
          out-of-area reader the waitlist that already exists. */}
      <p className="sw-estimate-note">
        Live in East London &amp; Mdantsane, expanding across South Africa.{' '}
        <a href="#coverage">Check coverage</a>
      </p>

      {/* Once booking works on the web, the widget's action is the booking
          itself. Until then it is the store link, because the site must not
          offer a flow that is not live. Rendered conditionally rather than
          branched inside one component, so the marketing page does not pull in
          router and draft hooks it has no other use for. */}
      {WEB_BOOKING_ENABLED ? (
        <div className="sw-estimate-action-stack">
          <BookingCta
            distanceKm={priced ? activeEstimate.distanceKm : null}
            dropoff={dropoff}
            estimatedFare={priced ? (selected?.estimated_fare ?? null) : null}
            pickup={pickup}
            priced={priced}
            vehicleType={selected?.vehicleType ?? null}
          />
          <WhatsAppBookingLink
            className="sw-btn-secondary sw-whatsapp-estimate-cta"
            dropoff={dropoffText}
            dropoffLat={whatsappDropoffPoint?.lat ?? null}
            dropoffLng={whatsappDropoffPoint?.lng ?? null}
            pickup={pickupText}
            pickupLat={whatsappPickupPoint?.lat ?? null}
            pickupLng={whatsappPickupPoint?.lng ?? null}
            placement="estimate_widget"
          />
        </div>
      ) : (
        <div className="sw-estimate-action-stack">
          <AppStoreLink
            className="sw-btn-primary sw-estimate-cta"
            eventParams={{ priced_route: priced }}
            placement="estimate_widget"
          />
          <WhatsAppBookingLink
            className="sw-btn-secondary sw-whatsapp-estimate-cta"
            dropoff={dropoffText}
            dropoffLat={whatsappDropoffPoint?.lat ?? null}
            dropoffLng={whatsappDropoffPoint?.lng ?? null}
            pickup={pickupText}
            pickupLat={whatsappPickupPoint?.lat ?? null}
            pickupLng={whatsappPickupPoint?.lng ?? null}
            placement="estimate_widget"
          />
        </div>
      )}

      {/* Two different things, and the site used to name the wrong channel for
          both. Confirming the fare happens wherever you book — the web can now
          take that step. Approving a driver's counter-offer still cannot: that
          lives in the app (see TripDetailPage), so it is the only half that
          still names one. */}
      <p className="sw-estimate-note">
        You confirm the fare before dispatch. Large, heavy, difficult-access, or multi-load
        jobs may receive a driver-proposed fare to approve — that approval happens in the app.
      </p>
    </div>
  );
};

interface PlaceEstimateFieldProps {
  dotClassName: string;
  label: string;
  placesReady: boolean;
  text: string;
  onTextChange: (value: string) => void;
  onPlaceSelected: (point: EstimatePoint) => void;
}

const PlaceEstimateField: React.FC<PlaceEstimateFieldProps> = ({
  dotClassName,
  label,
  placesReady,
  text,
  onTextChange,
  onPlaceSelected,
}) => {
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [open, setOpen] = useState(false);
  /* -1 means "typing, nothing highlighted" — the combobox pattern keeps focus on
     the input and moves a virtual cursor via aria-activedescendant, so a screen
     reader announces each suggestion without the field losing focus. */
  const [activeIndex, setActiveIndex] = useState(-1);
  const autocompleteRef = useRef<GoogleAutocompleteService | null>(null);
  const placesRef = useRef<GooglePlacesService | null>(null);
  const placesNodeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!placesReady || !window.google?.maps?.places) return;
    autocompleteRef.current = new window.google.maps.places.AutocompleteService();
    placesRef.current = new window.google.maps.places.PlacesService(
      placesNodeRef.current ?? document.createElement('div'),
    );
  }, [placesReady]);

  useEffect(() => {
    if (!placesReady || !autocompleteRef.current || text.trim().length < 3) {
      setPredictions([]);
      return;
    }

    let cancelled = false;
    const timeout = window.setTimeout(() => {
      autocompleteRef.current?.getPlacePredictions(
        {
          input: text,
          componentRestrictions: { country: 'za' },
          fields: ['place_id', 'description'],
        },
        (items) => {
          if (cancelled) return;
          setPredictions(items ?? []);
          /* A new result set invalidates the old highlight. */
          setActiveIndex(-1);
        },
      );
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [placesReady, text]);

  const selectPrediction = (prediction: PlacePrediction) => {
    if (!placesRef.current) return;
    placesRef.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['formatted_address', 'geometry', 'name'],
      },
      (place, status) => {
        const okStatus = window.google?.maps?.places?.PlacesServiceStatus?.OK;
        if (status !== okStatus || !place?.geometry?.location) return;
        const address = place.formatted_address || prediction.description;
        onPlaceSelected({
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          address,
        });
        setPredictions([]);
        setOpen(false);
      },
    );
  };

  const fieldId = `estimate-${label.toLowerCase()}`;
  const listId = `${fieldId}-options`;
  const visible = predictions.slice(0, 5);
  const menuOpen = open && visible.length > 0;
  const optionId = (index: number) => `${listId}-${index}`;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
      return;
    }

    if (!menuOpen) {
      /* Down-arrow on a closed field re-opens results the visitor dismissed,
         rather than stranding them with no way back to the list. */
      if (event.key === 'ArrowDown' && visible.length) setOpen(true);
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % visible.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => (current <= 0 ? visible.length - 1 : current - 1));
      return;
    }

    if (event.key === 'Enter' && activeIndex >= 0) {
      /* Only swallow Enter when a suggestion is highlighted, so the key still
         behaves normally for anyone typing an address and ignoring the list. */
      event.preventDefault();
      selectPrediction(visible[activeIndex]);
    }
  };

  return (
    <div className="sw-place-field">
      <div className="sw-route-field sw-route-field-input">
        <span className={`sw-route-dot ${dotClassName}`} aria-hidden="true" />
        <label className="sw-route-label" htmlFor={fieldId}>
          {label}
        </label>
        {/* Wired as an ARIA 1.2 combobox. The suggestions were previously
            buttons inside a role="listbox", which is invalid: a listbox may only
            own options, so assistive tech announced a control with no readable
            structure — on the site's primary conversion surface. */}
        <input
          aria-activedescendant={
            menuOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined
          }
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded={menuOpen}
          autoComplete="off"
          className="sw-route-input"
          disabled={!placesReady}
          id={fieldId}
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          onChange={(event) => {
            onTextChange(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placesReady ? `Search ${label.toLowerCase()}` : shortAddress(text)}
          role="combobox"
          value={text}
        />
      </div>
      <div ref={placesNodeRef} hidden />
      {/* Read by visitors, so it must describe their situation rather than a
          developer's — this previously told them to restart the dev server. */}
      {!placesReady ? (
        <p className="sw-place-hint">
          Address search is unavailable right now. The estimate below is for the sample route.
        </p>
      ) : null}
      <div className="sw-place-menu" hidden={!menuOpen} id={listId} role="listbox">
        {visible.map((prediction, index) => (
          <button
            aria-selected={index === activeIndex}
            className="sw-place-option"
            id={optionId(index)}
            key={prediction.place_id}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => selectPrediction(prediction)}
            onMouseEnter={() => setActiveIndex(index)}
            role="option"
            type="button"
          >
            {prediction.description}
          </button>
        ))}
      </div>
    </div>
  );
};

export default EstimateWidget;
